import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { PrivateKeyRecoveryOptionStatusEnum } from '#src/__generated__/globalTypes.ts';
import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { IconButton } from '#src/atoms/buttons/IconButton/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconAlert } from '#src/atoms/icons/system/iconAlert.ts';
import { iconAlertCircle } from '#src/atoms/icons/system/iconAlertCircle.ts';
import { iconTrash } from '#src/atoms/icons/system/iconTrash.ts';
import { SBHorizontal } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, BodyS, HeadlineS } from '#src/atoms/typography/index.tsx';
import { DialogWithNavigation } from '#src/components/dialog/DialogWithNavigation/index.tsx';
import { useScreenSize } from '#src/hooks/device/useScreenSize.ts';
import { useDeactivateWalletRecoveryEmail } from '#src/hooks/recovery/useDeactivateWalletRecoveryEmail.ts';
import { useRecoveryOptions } from '#src/hooks/recovery/useRecoveryOptions.ts';
import { useResendVerificationCodeForRecoveryEmail } from '#src/hooks/recovery/useResendVerificationCodeForRecoveryEmail.ts';
import { glossary } from '#src/lib/glossary.ts';

import { AddRecoveryEmailForm } from './AddRecoveryEmailForm/index.tsx';
import { VerifyRecoveryEmailForm } from './VerifyRecoveryEmailForm/index.tsx';

const messages = defineMessages({
  title: {
    defaultMessage: 'Recovery email(s)',
  },
  helperWithoutRecoveryEmails: {
    defaultMessage:
      'Consider adding a recovery email. It will help us contact you if you get locked out or to help you recover your Sorare wallet.',
  },
  helperWithRecoveryEmails: {
    defaultMessage:
      'We can contact you to this email if you get locked out or to help you recover your Sorare wallet.',
  },
  submit: {
    defaultMessage: 'Add recovery emails',
  },
});

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const StyledText16 = styled(BodyM)`
  display: flex;
  align-items: center;
  gap: var(--unit);
`;

const StyledButtonBase = styled(ButtonBase)`
  color: var(--c-link);
  text-decoration: underline;
`;

export const RecoveryEmail = () => {
  const { up: isTablet } = useScreenSize('tablet');
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [showVerify, setShowVerify] = useState<boolean>(false);
  const [currentRecoveryEmail, setCurrentRecoveryEmail] = useState<
    string | null
  >(null);
  const { recoveryEmails, allActiveRecoveryEmailsArePrivateRelay } =
    useRecoveryOptions();
  const { deactivateWalletRecoveryEmail } = useDeactivateWalletRecoveryEmail();
  const { resendVerificationCodeForRecoveryEmail } =
    useResendVerificationCodeForRecoveryEmail();
  const hasRecoveryEmails = !!recoveryEmails.length;
  const hasNotVerifiedRecoveryEmail = !recoveryEmails.find(
    r => r.status === PrivateKeyRecoveryOptionStatusEnum.ACTIVE
  );
  const header = {
    title: messages.title,
    description: hasRecoveryEmails
      ? messages.helperWithRecoveryEmails
      : messages.helperWithoutRecoveryEmails,
  };

  const addRecoveryEmail = () => {
    setCurrentRecoveryEmail(null);
    setOpenDialog(true);
  };

  const resendCode = (reference: string, destination: string) => {
    resendVerificationCodeForRecoveryEmail(reference);
    setCurrentRecoveryEmail(destination);
    setOpenDialog(true);
    setShowVerify(true);
  };
  const onClose = () => {
    setOpenDialog(false);
  };

  return (
    <div>
      <StyledText16>
        {allActiveRecoveryEmailsArePrivateRelay ? (
          <FontAwesomeIcon icon={iconAlert} color="var(--c-yellow-500)" />
        ) : (
          !!hasNotVerifiedRecoveryEmail && (
            <FontAwesomeIcon icon={iconAlertCircle} color="var(--c-red-500)" />
          )
        )}{' '}
        <FormattedMessage {...header.title} />
      </StyledText16>
      {allActiveRecoveryEmailsArePrivateRelay ? (
        <BodyS color="var(--c-yellow-500)">
          <FormattedMessage defaultMessage="Your recovery email is an Apple Private Relay address, which can be deactivated by Apple at any time. We recommend adding a personal email to ensure you can always recover your wallet." />
        </BodyS>
      ) : null}
      {!!hasRecoveryEmails && (
        <List>
          {recoveryEmails.map(email => (
            <li key={email.id}>
              <SBHorizontal gap={0}>
                <BodyS color="var(--c-nd-600)">{email.destination}</BodyS>
                <BodyS color="var(--c-red-400)">
                  <IconButton
                    icon={iconTrash}
                    size="small"
                    color="transparent"
                    onClick={() => {
                      deactivateWalletRecoveryEmail(email.destination);
                    }}
                  />
                </BodyS>
              </SBHorizontal>
              {email.status ===
                PrivateKeyRecoveryOptionStatusEnum.PENDING_VALIDATION && (
                <>
                  <BodyS color="var(--c-nd-600)">
                    <FormattedMessage
                      defaultMessage="{icon} Pending verification"
                      values={{
                        icon: (
                          <FontAwesomeIcon
                            color="var(--c-red-500)"
                            icon={iconAlertCircle}
                          />
                        ),
                      }}
                    />
                  </BodyS>
                  <button
                    type="button"
                    onClick={() => resendCode(email.id, email.destination)}
                  >
                    <BodyS color="var(--c-blue-400)">
                      <FormattedMessage {...glossary.resendVerificationCode} />
                    </BodyS>
                  </button>
                </>
              )}
            </li>
          ))}
        </List>
      )}
      <div>
        <StyledButtonBase onClick={addRecoveryEmail}>
          <BodyS bold>
            <FormattedMessage {...messages.submit} />
          </BodyS>
        </StyledButtonBase>
      </div>
      <DialogWithNavigation
        open={openDialog}
        onBackButton={() =>
          !currentRecoveryEmail ? onClose() : setCurrentRecoveryEmail(null)
        }
        title={
          <HeadlineS as="h5">
            <FormattedMessage {...glossary.recoveryEmail} />
          </HeadlineS>
        }
        onClose={onClose}
        fullScreen={!isTablet}
      >
        {showVerify && currentRecoveryEmail ? (
          <VerifyRecoveryEmailForm
            email={currentRecoveryEmail}
            onSuccess={() => {
              setCurrentRecoveryEmail(null);
              setShowVerify(false);
              onClose();
            }}
          />
        ) : (
          <AddRecoveryEmailForm
            email={currentRecoveryEmail}
            onSuccess={(email: string) => {
              setCurrentRecoveryEmail(email);
              setShowVerify(true);
            }}
          />
        )}
      </DialogWithNavigation>
    </div>
  );
};
